/* eslint-disable @typescript-eslint/no-unsafe-assignment */
export interface MapboxFeature {
  id: string
  type: string
  place_type: string[]
  relevance: number
  properties: {
    wikidata?: string
    short_code?: string
    address?: string
    category?: string
    maki?: string
    tel?: string
  }
  text: string
  place_name: string
  bbox?: number[]
  center: number[] // [longitude, latitude]
  geometry: {
    type: string
    coordinates: number[] // [longitude, latitude]
  }
  context?: Array<{
    id: string
    short_code?: string
    wikidata?: string
    text: string
  }>
}

export interface MapboxGeocodingResponse {
  type: string
  query: string[]
  features: MapboxFeature[]
  attribution: string
}

export interface LocationSearchResult {
  id: string
  place_name: string
  text: string
  center: [number, number] // [longitude, latitude]
  relevance: number
}

/**
 * Search for locations using Mapbox Geocoding API
 */
export async function searchLocations(
  query: string,
  accessToken: string,
  options: {
    country?: string
    types?: string
    limit?: number
  } = {}
): Promise<LocationSearchResult[]> {
  if (!query.trim()) {
    return []
  }

  const { country = 'us', types = 'place,address,poi', limit = 5 } = options

  const params = new URLSearchParams({
    access_token: accessToken,
    q: query,
    country,
    types,
    limit: limit.toString(),
    autocomplete: 'true',
  })

  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
    query
  )}.json?${params.toString()}`

  const response = await fetch(url)

  if (!response.ok) {
    // Try to get error details from response
    let errorDetails = ''
    try {
      const errorData = await response.json() as { message?: string }
      errorDetails = errorData.message ? ` - ${errorData.message}` : ''
    } catch {
      // Ignore JSON parse errors
    }

    // For authentication errors, throw so caller can handle appropriately
    if (response.status === 401) {
      const error = new Error(`Mapbox API authentication failed (401)${errorDetails}. Check that access_token is valid.`)
        ; (error as Error & { status: number }).status = 401
      throw error
    }

    // For other errors, also throw but with different status
    const error = new Error(`Mapbox API error: ${response.status}${errorDetails}`)
      ; (error as Error & { status: number }).status = response.status
    throw error
  }

  const data: MapboxGeocodingResponse = await response.json()

  return data.features.map((feature) => ({
    id: feature.id,
    place_name: feature.place_name,
    text: feature.text,
    center: feature.center as [number, number],
    relevance: feature.relevance,
  }))
}

/**
 * Reverse geocode coordinates to get location information
 */
export async function reverseGeocode(
  longitude: number,
  latitude: number,
  accessToken: string
): Promise<LocationSearchResult | null> {
  const params = new URLSearchParams({
    access_token: accessToken,
    types: 'place,address',
  })

  try {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?${params.toString()}`
    )

    if (!response.ok) {
      throw new Error(`Mapbox API error: ${response.status}`)
    }

    const data: MapboxGeocodingResponse = await response.json()

    if (data.features.length === 0) {
      return null
    }

    const feature = data.features[0]
    if (!feature) {
      return null
    }

    return {
      id: feature.id,
      place_name: feature.place_name,
      text: feature.text,
      center: feature.center as [number, number],
      relevance: feature.relevance,
    }
  } catch (error) {
    console.error('Error reverse geocoding:', error)
    return null
  }
}
